import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { QrCode, X, Smartphone } from "lucide-react";

/**
 * QRCodeButton — opens a modal with a large, scannable QR code of the supplied
 * URL. Designed for the live dealer pitch: hand the dealer principal their own
 * phone, they scan, the customer-facing quote loads on their device in <2 seconds.
 *
 * Use anywhere you'd otherwise paste a share URL into a chat — JobEditor's
 * "Customer link" card, dashboard quick-pitch buttons, the demo walkthrough.
 */
export function QRCodeButton({ url, label = "Scan to view on phone", testId = "qr-code-btn", caption }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-outline"
        data-testid={testId}
        title="Show a QR code for the customer link"
      >
        <QrCode className="w-4 h-4" />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="qr-code-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-md p-6 animate-fade-up"
          >
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="label-overline text-primary">Scan with your phone</div>
                <div className="font-display font-bold text-2xl tracking-tight mt-1 flex items-center gap-2">
                  <Smartphone className="w-6 h-6 text-primary" />
                  Customer quote
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-full w-9 h-9 grid place-items-center hover:bg-muted text-muted-foreground"
                data-testid="qr-code-close-btn"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {caption && (
              <div className="mt-3 text-sm text-muted-foreground">{caption}</div>
            )}

            <div className="mt-5 flex items-center justify-center">
              <div className="bg-white p-5 rounded-lg shadow-inner">
                <QRCodeSVG
                  value={url}
                  size={260}
                  level="M"
                  marginSize={1}
                  data-testid="qr-code-svg"
                />
              </div>
            </div>

            <div className="mt-5 text-[11px] font-mono-plex break-all text-muted-foreground bg-muted/50 p-2 rounded text-center" data-testid="qr-code-url">
              {url}
            </div>

            <div className="mt-4 text-xs text-muted-foreground text-center">
              Open your phone's camera, point it at the QR code and tap the link.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default QRCodeButton;
